/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import Badge from "react-bootstrap/Badge";
import Button from "react-bootstrap/Button";
import Card from "react-bootstrap/Card";
import Form from "react-bootstrap/Form";
import Modal from "react-bootstrap/Modal";
import Spinner from "react-bootstrap/Spinner";
import Table from "react-bootstrap/Table";
import Helmet from "react-helmet";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import {
  fetchClubEmployees,
  inviteClubEmployee,
  postClubEmployeeResendInvite,
} from "../../api/clubApi";
import {
  asClubEmployeesList,
  formatClubEmployeeDisplayName,
} from "../../helpers/clubEmployeeList";
import {
  canShowResendInvite,
  pickClubStaffUserId,
  pickInviteStatusKey,
  pickStaffEmail,
} from "../../helpers/clubStaffInvite";
import Breadcrumbs from "../../components/customBreadcrumb/customBreadcrumb";
import CustomButton from "../../components/customButton/customButton";
import useAuth from "../../hooks/useAuth";
import { clubListResponce } from "../../store/features/club/clubSlice";
import { showToast } from "../../store/features/toast/toastSlice";
import Constant from "../../util/constant";

/**
 * CLUB_MANAGER: invite and list employees for the managed club.
 */
const ClubEmployeesContainer = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { managedClubId } = useAuth();
  const clubList = useSelector((s) => s?.club?.clubList ?? []);

  const [email, setEmail] = useState("");
  const [firstname, setFirstname] = useState("");
  const [lastname, setLastname] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [resendTarget, setResendTarget] = useState(null);
  const [resendingUserId, setResendingUserId] = useState(null);

  useEffect(() => {
    dispatch(clubListResponce());
  }, [dispatch]);

  const clubLabel = useMemo(() => {
    const c = (clubList || []).find(
      (x) => String(x._id) === String(managedClubId)
    );
    return c?.name || c?.slug || managedClubId || "—";
  }, [clubList, managedClubId]);

  const loadEmployees = useCallback(async () => {
    if (!managedClubId) {
      setRows([]);
      return;
    }
    setLoading(true);
    try {
      const res = await fetchClubEmployees(managedClubId);
      const raw = res?.data ?? res;
      let list = asClubEmployeesList(raw);
      if (
        list.length === 0 &&
        raw &&
        typeof raw === "object" &&
        raw.data != null
      ) {
        list = asClubEmployeesList(raw.data);
      }
      setRows(list);
    } catch (err) {
      dispatch(
        showToast({
          show: false,
          message:
            typeof err === "string"
              ? err
              : err?.message || t("CLUB_EMPLOYEES_LOAD_ERROR"),
          type: "error",
        })
      );
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [managedClubId, dispatch, t]);

  useEffect(() => {
    loadEmployees();
  }, [loadEmployees]);

  const onInvite = (e) => {
    e.preventDefault();
    if (!managedClubId) return;
    if (!Constant.REGEX.EMAIL.test(String(email || "").trim())) {
      dispatch(
        showToast({
          show: false,
          message: t("CLUB_INVITE_VALIDATION"),
          type: "error",
        })
      );
      return;
    }
    const firstTrim = String(firstname || "").trim();
    const lastTrim = String(lastname || "").trim();
    if (!firstTrim) {
      dispatch(
        showToast({
          show: false,
          message: t("CLUB_EMPLOYEE_INVITE_FIRSTNAME_REQUIRED"),
          type: "error",
        })
      );
      return;
    }
    if (firstTrim.length > 100) {
      dispatch(
        showToast({
          show: false,
          message: t("CLUB_EMPLOYEE_INVITE_FIRSTNAME_MAX"),
          type: "error",
        })
      );
      return;
    }
    if (!lastTrim) {
      dispatch(
        showToast({
          show: false,
          message: t("CLUB_EMPLOYEE_INVITE_LASTNAME_REQUIRED"),
          type: "error",
        })
      );
      return;
    }
    if (lastTrim.length > 100) {
      dispatch(
        showToast({
          show: false,
          message: t("CLUB_EMPLOYEE_INVITE_LASTNAME_MAX"),
          type: "error",
        })
      );
      return;
    }
    setBusy(true);
    inviteClubEmployee({
      email: String(email).trim(),
      firstname: firstTrim,
      lastname: lastTrim,
      clubId: managedClubId,
    })
      .then(() => {
        dispatch(
          showToast({
            show: false,
            message: t("CLUB_EMPLOYEE_INVITE_SENT"),
            type: "success",
          })
        );
        setEmail("");
        setFirstname("");
        setLastname("");
        loadEmployees();
      })
      .catch((err) => {
        dispatch(
          showToast({
            show: false,
            message:
              typeof err === "string"
                ? err
                : err?.message || t("CLUB_EMPLOYEE_INVITE_ERROR"),
            type: "error",
          })
        );
      })
      .finally(() => setBusy(false));
  };

  const closeResendConfirm = () => {
    if (resendingUserId) return;
    setResendTarget(null);
  };

  const onConfirmResendInvite = async () => {
    const userId = pickClubStaffUserId(resendTarget);
    const email = pickStaffEmail(resendTarget);
    if (!userId || resendingUserId) return;
    setResendingUserId(userId);
    try {
      await postClubEmployeeResendInvite(userId);
      dispatch(
        showToast({
          show: false,
          message: t("CLUB_INVITE_RESEND_OK", { email: email || "—" }),
          type: "success",
        })
      );
      setResendTarget(null);
      loadEmployees();
    } catch (err) {
      const message =
        typeof err === "string"
          ? err
          : err?.message || t("CLUB_INVITE_RESEND_ERROR");
      dispatch(
        showToast({
          show: false,
          message,
          type: "error",
        })
      );
      if (
        typeof message === "string" &&
        message.toLowerCase().includes("already signed in")
      ) {
        setResendTarget(null);
        loadEmployees();
      }
    } finally {
      setResendingUserId(null);
    }
  };

  const parseEmployeeRow = (r, i) => {
    const first = String(r.firstname ?? r.user?.firstname ?? "").trim();
    const last = String(r.lastname ?? r.user?.lastname ?? "").trim();
    const legacyName = !first && !last ? formatClubEmployeeDisplayName(r) : "";
    const userId = pickClubStaffUserId(r);
    const rowEmail = pickStaffEmail(r);
    const empref = r.empref ?? r.user?.empref ?? "—";
    const showResend = canShowResendInvite(r);
    const resendBusy = userId && resendingUserId === userId;
    const displayName =
      [first, last].filter(Boolean).join(" ") || legacyName || "—";
    return {
      key: r._id || r.id || i,
      index: i + 1,
      first: first || legacyName || "—",
      last: last || "—",
      displayName,
      userId,
      rowEmail: rowEmail || "—",
      empref,
      showResend,
      resendBusy,
      row: r,
    };
  };

  const renderResendButton = (item) => {
    if (!item.showResend) {
      return <span className="text-muted small">—</span>;
    }
    return (
      <Button
        variant="link"
        size="sm"
        className="p-0 text-decoration-none"
        disabled={Boolean(resendingUserId)}
        onClick={() => setResendTarget(item.row)}
      >
        {item.resendBusy ? (
          <>
            <Spinner animation="border" size="sm" className="me-1" />
            {t("LOADING")}
          </>
        ) : (
          t("CLUB_INVITE_RESEND")
        )}
      </Button>
    );
  };

  const renderInviteStatusBadge = (row) => {
    const key = pickInviteStatusKey(row);
    if (key === "INVITED") {
      return (
        <Badge bg="warning" text="dark" className="fw-normal">
          {t("CLUB_INVITE_STATUS_PENDING")}
        </Badge>
      );
    }
    if (key === "ACTIVE") {
      return (
        <Badge bg="success" className="fw-normal">
          {t("CLUB_INVITE_STATUS_ACTIVE")}
        </Badge>
      );
    }
    return <span className="text-muted">—</span>;
  };

  const pageTitle = [{ title: t("CLUB_EMPLOYEES_PAGE_TITLE"), path: "#" }];

  if (!managedClubId) {
    return (
      <div className="pageContent categoryManagement club-staff-page-shell p-4">
        <Helmet>
          <title>{t("CLUB_EMPLOYEES_PAGE_TITLE")} | RizzTix</title>
        </Helmet>
        <p className="text-danger">{t("CLUB_WEBSITE_NO_CLUB")}</p>
      </div>
    );
  }

  return (
    <div className="pageContent categoryManagement club-staff-page-shell club-staff-invite-page">
      <Helmet>
        <title>{t("CLUB_EMPLOYEES_PAGE_TITLE")} | RizzTix</title>
      </Helmet>
      <Breadcrumbs
        innerPagetitle={t("CLUB_EMPLOYEES_PAGE_TITLE")}
        breadcrumbItem={t("CLUB_EMPLOYEES_PAGE_TITLE")}
        pageTitle={pageTitle}
        BreadcrumbInner={true}
        BreadcrumbmainTitle={true}
      />
      <Card className="mb-3">
        <Card.Body>
          <p className="text-muted small">
            {t("CLUB_EMPLOYEES_CLUB_LABEL")}: <strong>{clubLabel}</strong>
          </p>
          <h6 className="club-staff-section-title">
            {t("CLUB_EMPLOYEE_INVITE_HEADING")}
          </h6>
          <Form onSubmit={onInvite} className="row g-2 align-items-end flex-wrap">
            <div className="col-12 col-md-3">
              <Form.Label>
                {t("CLUB_EMPLOYEE_FIRSTNAME")}{" "}
                <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                value={firstname}
                onChange={(e) => setFirstname(e.target.value)}
                placeholder={t("CLUB_EMPLOYEE_FIRSTNAME_PH")}
                autoComplete="given-name"
                maxLength={100}
                required
              />
            </div>
            <div className="col-12 col-md-3">
              <Form.Label>
                {t("CLUB_EMPLOYEE_LASTNAME")}{" "}
                <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                value={lastname}
                onChange={(e) => setLastname(e.target.value)}
                placeholder={t("CLUB_EMPLOYEE_LASTNAME_PH")}
                autoComplete="family-name"
                maxLength={100}
                required
              />
            </div>
            <div className="col-12 col-md-4">
              <Form.Label>
                Email <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@example.com"
                autoComplete="email"
                required
              />
            </div>
            <div className="col-12 col-md-auto">
              <CustomButton
                type="submit"
                className="fillButton club-staff-invite-submit club-staff-btn-wide-mobile"
                disabled={busy}
              >
                {busy ? t("LOADING") : t("CLUB_EMPLOYEE_INVITE_SEND")}
              </CustomButton>
            </div>
          </Form>
          <p className="text-muted small mt-2 mb-0 club-staff-invite-help">
            {t("CLUB_EMPLOYEE_INVITE_HELP_SHORT")}
          </p>
        </Card.Body>
      </Card>
      <Card>
        <Card.Body>
          <h6 className="club-staff-section-title">{t("CLUB_EMPLOYEES_LIST")}</h6>
          {loading ? (
            <p className="text-muted small">{t("LOADING")}</p>
          ) : rows.length === 0 ? (
            <p className="text-muted small mb-0">{t("NO_DATA_FOUND")}</p>
          ) : (
            <>
              <div className="club-staff-team-table-wrap">
                <Table
                  bordered
                  hover
                  size="sm"
                  className="club-staff-team-table mb-0"
                >
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>{t("CLUB_EMPLOYEE_FIRSTNAME")}</th>
                      <th>{t("CLUB_EMPLOYEE_LASTNAME")}</th>
                      <th>{t("CLUB_EMPLOYEE_EMPREF")}</th>
                      <th>Email</th>
                      <th>{t("CLUB_INVITE_STATUS")}</th>
                      <th>{t("CLUB_INVITE_ACTIONS")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => {
                      const item = parseEmployeeRow(r, i);
                      return (
                        <tr key={item.key}>
                          <td>{item.index}</td>
                          <td>{item.first}</td>
                          <td>{item.last}</td>
                          <td className="font-monospace small">
                            {item.empref}
                          </td>
                          <td>{item.rowEmail}</td>
                          <td>{renderInviteStatusBadge(r)}</td>
                          <td>{renderResendButton(item)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>

              <div className="club-staff-team-cards">
                {rows.map((r, i) => {
                  const item = parseEmployeeRow(r, i);
                  return (
                    <article key={item.key} className="club-staff-team-card">
                      <div className="club-staff-team-card-head">
                        <div>
                          <div className="club-staff-team-card-index">
                            #{item.index}
                          </div>
                          <div className="club-staff-team-card-name">
                            {item.displayName}
                          </div>
                        </div>
                        {renderInviteStatusBadge(r)}
                      </div>
                      <dl className="club-staff-team-card-fields">
                        <div className="club-staff-team-card-field">
                          <dt>{t("CLUB_EMPLOYEE_EMPREF")}</dt>
                          <dd className="font-monospace">{item.empref}</dd>
                        </div>
                        <div className="club-staff-team-card-field">
                          <dt>Email</dt>
                          <dd>{item.rowEmail}</dd>
                        </div>
                      </dl>
                      <div className="club-staff-team-card-actions">
                        {renderResendButton(item)}
                      </div>
                    </article>
                  );
                })}
              </div>
            </>
          )}
          <CustomButton
            type="button"
            className="borderButton btn-sm mt-2 club-staff-btn-wide-mobile"
            onClick={loadEmployees}
          >
            {t("REFRESH")}
          </CustomButton>
        </Card.Body>
      </Card>

      <Modal
        show={resendTarget != null}
        onHide={closeResendConfirm}
        centered
        backdrop={resendingUserId ? "static" : true}
      >
        <Modal.Header closeButton={!resendingUserId}>
          <Modal.Title className="fs-6">
            {t("CLUB_INVITE_RESEND_CONFIRM_TITLE")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="small">
          {t("CLUB_INVITE_RESEND_CONFIRM_BODY", {
            email: pickStaffEmail(resendTarget) || "—",
          })}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            size="sm"
            onClick={closeResendConfirm}
            disabled={Boolean(resendingUserId)}
          >
            {t("CANCEL")}
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={onConfirmResendInvite}
            disabled={Boolean(resendingUserId)}
          >
            {resendingUserId ? t("LOADING") : t("CLUB_INVITE_RESEND")}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ClubEmployeesContainer;
